import {createAction, createAsyncThunk, createReducer} from '@reduxjs/toolkit';

import {putQuoteShippingV2} from 'api/magento/cart/cart';
import {ENDPOINTS} from 'constants/endpoints';
import {ApplicationStoreType} from 'desktop/store';
import {apiConfig} from 'libs/api/api';
import {flags} from 'libs/rollout';
import {ShippingMethodsListType, ShippingMethodsType} from 'types/shippingMethodsType';

import {isOnlyContactLensesInOrderReview} from '../../../selectors/orderReviewItems/orderReviewItems';

export const initialState: ShippingMethodsListType = {
    error: '',
    list: [],
    selected: '',
    status: '',
};

type GetShippingMethodParamsType = {
    country: string;
    postcode?: string;
    region_id?: string;
    contactsCustomized?: `${boolean}`;
};

export const saveShippingMethod = createAsyncThunk<
    void,
    string,
    {rejectValue: {response: {data: {messages: {error: [{code: number; message: string}]}}}}}
>('CHECKOUT@SAVE_SHIPPING_METHOD', async (code, {rejectWithValue}) => {
    try {
        return await putQuoteShippingV2('regular', {shipping_method: code});
    } catch (e) {
        return rejectWithValue(e);
    }
});

export const getShippingMethod = createAsyncThunk<
    ShippingMethodsType,
    GetShippingMethodParamsType,
    {rejectValue: {response: {data: {message: string}}}}
>('CHECKOUT@GET_SHIPPING_METHOD', async (params, {getState, rejectWithValue}) => {
    const state = getState() as ApplicationStoreType;

    if (!params.region_id) delete params.region_id;

    if (isOnlyContactLensesInOrderReview(state) && flags.dCheckoutCLCustomShipping.isEnabled()) {
        params.contactsCustomized = 'true';
    }

    try {
        return await apiConfig.instanceGusaBackend.request({
            method: 'get',
            url: `${ENDPOINTS.CHECKOUT_GET_SHIPPING}?${new URLSearchParams(params).toString()}`,
        });
    } catch (e) {
        return rejectWithValue(e);
    }
});

export const updateShippingMethod = createAction<string>('CHECKOUT@UPDATE_SHIPPING_METHOD');

export const setShippingMethod = createAction<ShippingMethodsListType>(
    'CHECKOUT@SET_SHIPPING_METHOD',
);

/**
 * Shipping methods reducer
 *
 * @return {ShippingMethodsType} List of shipping methods
 */
export const shippingMethods = createReducer(initialState, builder =>
    builder
        .addCase(getShippingMethod.fulfilled, state => ({
            ...state,
            status: 'FULFILLED',
        }))
        .addCase(getShippingMethod.pending, state => ({
            ...state,
            status: 'PENDING',
        }))
        .addCase(getShippingMethod.rejected, (state, action) => ({
            ...state,
            error:
                action.payload?.response?.data?.message ||
                'Something went wrong. Please try again later.',
            status: 'REJECTED',
        }))
        .addCase(saveShippingMethod.fulfilled, state => ({
            ...state,
            status: 'FULFILLED',
        }))
        .addCase(saveShippingMethod.pending, state => ({
            ...state,
            status: 'PENDING',
        }))
        .addCase(saveShippingMethod.rejected, (state, action) => {
            return {
                ...state,
                error:
                    action.payload?.response?.data?.messages?.error?.[0]?.message ||
                    'Something went wrong. Please try again later.',
                selected: state.list.find(item => item.selected)?.code || '',
                status: 'REJECTED',
            };
        })
        .addCase(setShippingMethod, (state, action) => ({
            ...state,
            ...action.payload,
        }))
        .addCase(updateShippingMethod, (state, action) => ({
            ...state,
            selected: action.payload,
        })),
);
